import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ErpTemplate from '../../components/ErpTemplate';
import {
    FaSave, FaCalculator, FaTrash, FaFileInvoice,
    FaDownload, FaUpload, FaRobot, FaFilePdf, FaFileCode, FaPlus,
} from 'react-icons/fa';
import api from '../../services/api';
import { useHistory, useParams } from 'react-router-dom';
import ReactSelect from '../../components/ReactSelect';
import { NumericFormat } from 'react-number-format';
import { moeda, virgulaPonto } from '../../util/util';
import { toast } from 'react-toastify';
import '../../css/financeiro-parcela-pagar.css';

const TIPO_OPTIONS = [
    { value: 'nfse', label: 'NFS-e (Nota Fiscal de Serviços Eletrônica)' },
    { value: 'nfe', label: 'NF-e (Nota Fiscal Eletrônica)' },
    { value: 'recibo', label: 'Recibo' },
    { value: 'outros', label: 'Outros' },
];

const STATUS_OPTIONS = [
    { value: 'aguardando', label: '⏳ Aguardando recebimento' },
    { value: 'recebida', label: '📬 Recebida' },
    { value: 'aprovada', label: '✅ Aprovada' },
    { value: 'rejeitada', label: '❌ Rejeitada' },
    { value: 'cancelada', label: '🚫 Cancelada' },
    { value: 'substituida', label: '🔄 Substituída' },
];

const TRIBUTOS_LABELS = {
    irrf: 'IRRF',
    iss: 'ISS',
    pis: 'PIS',
    cofins: 'COFINS',
    csll: 'CSLL',
    inss: 'INSS',
    ibs: 'IBS',
    cbs: 'CBS',
};

const dadosIniciais = {
    numero: '',
    serie: '',
    tipo: 'nfse',
    status: 'aguardando',
    data_emissao: '',
    data_competencia: '',
    is_retroativa: false,
    fornecedor_id: '',
    nomeFornecedor: '',
    evento_id: '',
    nomeEvento: '',
    orcamento_id: '',
    nomeOrcamento: '',
    chave_nfe: '',
    protocolo_nfs: '',
    link_nfs: '',
    valor_bruto: '',
    valor_adiantamento: 0,
    valor_base_calculo: 0,
    valor_retencoes: 0,
    valor_liquido: 0,
    nbs: '',
    arquivo_id: '',
    nomeArquivo: '',
    linkArquivo: '',
    observacao: '',
    retencoes: [],
    substituida_por_id: null,
    substituida_por_label: '',
};

export default function CadastroNotaFiscal() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    const history = useHistory();
    const { id } = useParams();
    const editando = Boolean(id);

    const [dados, setDados] = useState(dadosIniciais);
    const [salvando, setSalvando] = useState(false);
    const [calculando, setCalculando] = useState(false);
    const [uploadando, setUploadando] = useState(false);
    const [etapaExtracao, setEtapaExtracao] = useState(null);
    const [resultadoExtracao, setResultadoExtracao] = useState(null);
    const [camposAutoPreenchidos, setCamposAutoPreenchidos] = useState({});
    const [dragOver, setDragOver] = useState(false);
    const inputArquivoRef = useRef(null);
    const timerEtapaRef = useRef(null);
    const [itensVinculados, setItensVinculados] = useState([]);
    const [carregandoItens, setCarregandoItens] = useState(false);
    const [showFormVincular, setShowFormVincular] = useState(false);
    const [novoItem, setNovoItem] = useState({ orcamentoitem: null, valor_alocado: '', observacao: '' });
    const [salvandoItem, setSalvandoItem] = useState(false);
    const [itensOpcoes, setItensOpcoes] = useState([]);
    const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);

    // ── Carregar dados se editando ──────────────────────────────────────────
    useEffect(() => {
        if (editando) {
            api({ method: 'get', action: '/notaFiscal/' + id, token })
                .then(response => {
                    const nf = response.data.data;
                    setDados({
                        ...dadosIniciais,
                        ...nf,
                        nomeFornecedor: nf.fornecedor
                            ? (nf.fornecedor.nomefantasia || nf.fornecedor.nome || '')
                            : '',
                        nomeEvento: nf.evento ? nf.evento.nome : '',
                        nomeOrcamento: nf.orcamento ? (nf.orcamento.codigo + ' — ' + nf.orcamento.nome) : '',
                        nomeArquivo: nf.arquivo ? nf.arquivo.nome : '',
                        retencoes: nf.retencoes || [],
                        substituida_por_id: nf.substituida_por_id || null,
                        substituida_por_label: nf.substituida_por_label || '',
                    });
                    setItensVinculados(nf.itens || []);
                    carregarItensVinculados(nf.id);
                })
                .catch(() => toast.error('Erro ao carregar nota fiscal.'));
        }
        // eslint-disable-next-line
    }, [id]);

    // ── Extração automática (XML / PDF via IA) ──────────────────────────────
    const extrairArquivo = (file) => {
        if (!file) return;
        const ext = (file.name || '').split('.').pop().toLowerCase();
        if (!['xml', 'pdf'].includes(ext)) {
            toast.warning('Somente arquivos XML ou PDF são aceitos para extração automática.');
            return;
        }
        if (timerEtapaRef.current) clearTimeout(timerEtapaRef.current);
        setEtapaExtracao('enviando');
        setResultadoExtracao(null);
        setCamposAutoPreenchidos({});
        timerEtapaRef.current = setTimeout(
            () => setEtapaExtracao('analisando'),
            ext === 'xml' ? 400 : 1400
        );
        const formData = new FormData();
        formData.append('arquivo', file);
        api({ method: 'post', action: '/notaFiscal/extrairDados', token, data: formData, headers: { 'Content-Type': 'multipart/form-data' } })
            .then(response => {
                clearTimeout(timerEtapaRef.current);
                const resultado = response.data.data;
                const campos = resultado.campos_extraidos || {};
                const metodo = resultado.metodo || 'manual';
                const novosDados = JSON.parse(JSON.stringify(dados));
                const preenchidos = {};
                const mapear = {
                    numero: 'numero', serie: 'serie', tipo: 'tipo',
                    data_emissao: 'data_emissao', data_competencia: 'data_competencia',
                    valor_bruto: 'valor_bruto', chave_nfe: 'chave_nfe',
                    protocolo_nfs: 'protocolo_nfs', nbs: 'nbs',
                };
                Object.keys(mapear).forEach(campoApi => {
                    if (campos[campoApi] !== null && campos[campoApi] !== undefined && campos[campoApi] !== '') {
                        novosDados[mapear[campoApi]] = campos[campoApi];
                        preenchidos[mapear[campoApi]] = true;
                    }
                });
                if (campos.fornecedor_id && campos.fornecedor_nome) {
                    novosDados.fornecedor_id  = campos.fornecedor_id;
                    novosDados.nomeFornecedor = campos.fornecedor_nome;
                    novosDados.retencoes      = [];
                    preenchidos.fornecedor_id = true;
                }
                if (resultado.arquivo_id) {
                    novosDados.arquivo_id  = resultado.arquivo_id;
                    novosDados.nomeArquivo = resultado.arquivo_nome || file.name;
                    novosDados.linkArquivo = resultado.arquivo_link || '';
                    preenchidos.arquivo_id = true;
                }
                const finalizarExtracao = () => {
                    setDados(novosDados);
                    setCamposAutoPreenchidos(preenchidos);
                    setResultadoExtracao({
                        metodo,
                        cnpj_emitente: campos.cnpj_emitente || null,
                        nome_emitente: campos.nome_emitente || null,
                        totalCampos: Object.keys(preenchidos).filter(k => k !== 'arquivo_id').length,
                    });
                    setEtapaExtracao(null);
                    const msgs = {
                        xml:           '✅ XML lido com sucesso — dados extraídos automaticamente.',
                        gpt_vision:    '🤖 PDF analisado por GPT-4o — confira os campos e corrija se necessário.',
                        claude_vision: '🤖 PDF analisado por Claude — confira os campos e corrija se necessário.',
                        pdf_sem_chave: '⚠ PDF recebido, mas nenhuma chave de IA está configurada — preencha manualmente.',
                        manual:        'ℹ Arquivo salvo. Não foi possível extrair dados automaticamente.',
                    };
                    const msg = msgs[metodo] || msgs.manual;
                    if (['xml', 'gpt_vision', 'claude_vision'].includes(metodo)) toast.success(msg);
                    else toast.warning(msg);
                };
                if (campos.fornecedor_id) {
                    setEtapaExtracao('buscando');
                    timerEtapaRef.current = setTimeout(finalizarExtracao, 700);
                } else {
                    finalizarExtracao();
                }
            })
            .catch(() => {
                clearTimeout(timerEtapaRef.current);
                toast.error('Erro ao processar o arquivo.');
                setEtapaExtracao(null);
            });
    };

    // ── Helpers de estado ───────────────────────────────────────────────────
    const set = (campo, valor) => setDados(prev => ({ ...prev, [campo]: valor }));

    const setRetencao = (tipo, campo, valor) => {
        const novas = JSON.parse(JSON.stringify(dados.retencoes));
        const idx = novas.findIndex(r => r.tipo === tipo);
        if (idx >= 0) {
            novas[idx][campo] = valor;
            novas[idx].eh_override = true;
            if (campo === 'aliquota' || campo === 'base_calculo') {
                novas[idx].valor = parseFloat(
                    ((parseFloat(novas[idx].base_calculo) || 0) * (parseFloat(novas[idx].aliquota) || 0) / 100).toFixed(2)
                );
            }
        } else {
            novas.push({ tipo, aliquota: 0, base_calculo: dados.valor_base_calculo, valor: 0, eh_override: true, [campo]: valor });
        }
        const total = novas.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
        const liquido = Math.max(0, (parseFloat(dados.valor_bruto) || 0) - total);
        setDados(prev => ({ ...prev, retencoes: novas, valor_retencoes: total, valor_liquido: liquido }));
    };

    // ── Calcular retenções ──────────────────────────────────────────────────
    const calcularRetencoes = () => {
        if (!dados.fornecedor_id) { toast.warning('Selecione o fornecedor antes de calcular.'); return; }
        if (!dados.valor_bruto) { toast.warning('Informe o valor bruto antes de calcular.'); return; }
        setCalculando(true);
        api({
            method: 'post', action: '/notaFiscal/calcularRetencoes', token,
            data: {
                fornecedor_id: dados.fornecedor_id,
                valor_bruto: virgulaPonto(dados.valor_bruto),
                valor_adiantamento: dados.valor_adiantamento || 0,
            },
        })
            .then(response => {
                const resultado = response.data.data;
                setDados(prev => ({
                    ...prev,
                    retencoes: resultado.retencoes || [],
                    valor_base_calculo: resultado.valor_base_calculo || 0,
                    valor_retencoes: resultado.valor_retencoes || 0,
                    valor_liquido: resultado.valor_liquido || 0,
                }));
                if (resultado.aviso) toast.warning(resultado.aviso);
                else toast.success('Retenções calculadas a partir da classificação fiscal do fornecedor.');
                setCalculando(false);
            })
            .catch(() => { toast.error('Erro ao calcular retenções.'); setCalculando(false); });
    };

    // ── Upload de arquivo ───────────────────────────────────────────────────
    const uploadArquivo = (file) => {
        if (!file) return;
        setUploadando(true);
        const formData = new FormData();
        formData.append('arquivo', file);
        api({ method: 'post', action: '/arquivo', token, data: formData, headers: { 'Content-Type': 'multipart/form-data' } })
            .then(response => {
                const arq = response.data.data;
                setDados(prev => ({ ...prev, arquivo_id: arq.id, nomeArquivo: arq.nome || file.name }));
                toast.success('Arquivo enviado com sucesso.');
                setUploadando(false);
            })
            .catch(() => { toast.error('Erro ao enviar arquivo.'); setUploadando(false); });
    };

    // ── Salvar ──────────────────────────────────────────────────────────────
    const salvar = () => {
        if (!dados.fornecedor_id) { toast.warning('Selecione o fornecedor.'); return; }
        if (!dados.valor_bruto) { toast.warning('Informe o valor bruto.'); return; }
        setSalvando(true);
        const payload = {
            numero: dados.numero, serie: dados.serie, tipo: dados.tipo, status: dados.status,
            data_emissao: dados.data_emissao || null, data_competencia: dados.data_competencia || null,
            is_retroativa: dados.is_retroativa, fornecedor_id: dados.fornecedor_id,
            evento_id: dados.evento_id || null, orcamento_id: dados.orcamento_id || null,
            chave_nfe: dados.chave_nfe, protocolo_nfs: dados.protocolo_nfs, link_nfs: dados.link_nfs,
            valor_bruto: virgulaPonto(dados.valor_bruto), nbs: dados.nbs,
            arquivo_id: dados.arquivo_id || null, observacao: dados.observacao,
            substituida_por_id: dados.substituida_por_id || null,
            retencoes_override: dados.retencoes
                .filter(r => r.eh_override)
                .map(r => ({ tipo: r.tipo, aliquota: r.aliquota, valor: r.valor })),
        };
        const method = editando ? 'put' : 'post';
        const action = editando ? '/notaFiscal/' + id : '/notaFiscal';
        api({ method, action, token, data: payload })
            .then(response => {
                const nf = response.data.data;
                toast.success('Nota fiscal salva com sucesso!');
                setSalvando(false);
                if (!editando) history.push('/cadastroNotaFiscal/' + nf.id);
            })
            .catch(err => {
                setSalvando(false);
                const statusCode = err && err.response && err.response.status;
                const respData   = err && err.response && err.response.data && err.response.data.data;
                if (statusCode === 422 && respData && respData.nota_fiscal_id_existente) {
                    const nfId = respData.nota_fiscal_id_existente;
                    toast.error(
                        <span>
                            {respData.erro || 'Nota fiscal duplicada.'}{' '}
                            <a href={'/cadastroNotaFiscal/' + nfId} target="_blank" rel="noopener noreferrer"
                                style={{ color: '#fff', textDecoration: 'underline', fontWeight: 'bold' }}>
                                Ver NF existente →
                            </a>
                        </span>,
                        { autoClose: 8000 }
                    );
                } else {
                    toast.error('Erro ao salvar nota fiscal.');
                }
            });
    };

    // ── Itens do orçamento vinculados ───────────────────────────────────────
    const carregarItensVinculados = (nfId) => {
        const idAlvo = nfId || id;
        if (!idAlvo) return;
        setCarregandoItens(true);
        api({ method: 'get', action: '/notaFiscal/' + idAlvo + '/itens', token })
            .then(r => setItensVinculados(r.data?.data ?? []))
            .catch(() => toast.error('Erro ao carregar itens vinculados.'))
            .finally(() => setCarregandoItens(false));
    };

    const abrirFormVincular = () => {
        setShowFormVincular(true);
        setNovoItem({ orcamentoitem: null, valor_alocado: '', observacao: '' });
        setCarregandoOpcoes(true);
        const params = { financeiro: true };
        if (dados.evento_id) params.evento = dados.evento_id;
        if (dados.orcamento_id) params.orcamento = dados.orcamento_id;
        api({ method: 'get', action: '/orcamento/itensPendentesNotaFiscal', token, params })
            .then(r => {
                const itens = r.data?.data ?? [];
                const vinculadosIds = new Set(itensVinculados.map(v => String(v.orcamentoitem_id)));
                setItensOpcoes(
                    itens
                        .filter(i => !vinculadosIds.has(String(i.id)))
                        .map(i => ({
                            value: String(i.id),
                            label: [
                                i.codigoFornecedor || null,
                                i.produto ? i.produto.nome : null,
                                i.fornecedor ? (i.fornecedor.nomefantasia || i.fornecedor.nome) : null,
                            ].filter(Boolean).join(' · ')
                            + (i.totalFechamento ? ' — ' + moeda(i.totalFechamento) : ''),
                        }))
                );
            })
            .catch(() => toast.error('Erro ao buscar itens do orçamento.'))
            .finally(() => setCarregandoOpcoes(false));
    };

    const vincularItemNF = () => {
        if (!novoItem.orcamentoitem) { toast.warning('Selecione um item.'); return; }
        setSalvandoItem(true);
        api({
            method: 'post', action: '/notaFiscal/' + id + '/itens', token,
            data: {
                orcamentoitem_id: novoItem.orcamentoitem.value,
                valor_alocado: virgulaPonto(novoItem.valor_alocado) || 0,
                observacao: novoItem.observacao || null,
            },
        })
            .then(() => { toast.success('Item vinculado.'); setShowFormVincular(false); carregarItensVinculados(); })
            .catch(() => toast.error('Erro ao vincular item.'))
            .finally(() => setSalvandoItem(false));
    };

    const desvincularItemNF = (itemId, descricao) => {
        if (!window.confirm('Remover vínculo com "' + descricao + '"?')) return;
        api({ method: 'delete', action: '/notaFiscalItem/' + itemId, token })
            .then(() => { toast.success('Vínculo removido.'); carregarItensVinculados(); })
            .catch(() => toast.error('Erro ao remover vínculo.'));
    };

    // ── Helpers de render ───────────────────────────────────────────────────
    const estiloAutoPreenchido = (campo) =>
        camposAutoPreenchidos[campo]
            ? { background: 'rgba(21,128,61,0.10)', borderColor: 'var(--fin-ok)' }
            : {};

    const autoTag = (campo) =>
        camposAutoPreenchidos[campo]
            ? <span title="Auto-extraído" style={{ color: 'var(--fin-ok)', marginLeft: 4, fontSize: '0.8rem' }}>✦</span>
            : null;

    const valorBrutoNum = parseFloat(virgulaPonto(dados.valor_bruto) || '0') || 0;
    const valorLiquidoCalc = Math.max(0, valorBrutoNum - (dados.valor_retencoes || 0));

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">

                {/* ── Header ──────────────────────────────────────────────── */}
                <div className="fin-pp__head">
                    <div>
                        <h2 className="fin-pp__title">
                            {editando ? 'Editar Nota Fiscal' : 'Nova Nota Fiscal'}
                        </h2>
                        <p className="fin-pp__subtitle">Dados do documento fiscal</p>
                    </div>
                    <div className="fin-pp__actions">
                        <Link to="/notaFiscal" className="fin-pp__btn fin-pp__btn--pill">
                            <FaFileInvoice size={14} style={{ marginRight: 6 }} /> Painel de NFs
                        </Link>
                    </div>
                </div>

                {/* ── Zona de upload / extração automática ─────────────────── */}
                <div className="fin-pp__card" style={{
                    marginBottom: 16,
                    border: dragOver ? '2px dashed var(--fin-ac)' : '2px dashed var(--fin-acb)',
                    background: dragOver ? 'var(--fin-acw)' : '#f8fbff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                }}
                    onClick={() => inputArquivoRef.current && inputArquivoRef.current.click()}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => {
                        e.preventDefault();
                        setDragOver(false);
                        const file = e.dataTransfer.files[0];
                        if (file) extrairArquivo(file);
                    }}
                >
                    <div style={{ padding: '20px 24px', textAlign: 'center' }}>
                        <input
                            ref={inputArquivoRef}
                            type="file"
                            accept=".xml,.pdf"
                            style={{ display: 'none' }}
                            onChange={e => {
                                const file = e.target.files[0];
                                if (file) extrairArquivo(file);
                                e.target.value = '';
                            }}
                        />

                        {etapaExtracao ? (
                            (() => {
                                const passos = [
                                    { key: 'enviando',   label: 'Enviando arquivo...' },
                                    { key: 'analisando', label: 'Extraindo dados do documento...' },
                                    { key: 'buscando',   label: 'Identificando fornecedor pelo CNPJ...' },
                                ];
                                const ordemAtual = ['enviando', 'analisando', 'buscando'].indexOf(etapaExtracao);
                                return (
                                    <div style={{ textAlign: 'left', display: 'inline-block' }}>
                                        {passos.map((passo, i) => {
                                            const feito  = i < ordemAtual;
                                            const ativo  = i === ordemAtual;
                                            const futuro = i > ordemAtual;
                                            return (
                                                <div key={passo.key} style={{
                                                    display: 'flex', alignItems: 'center', gap: 10,
                                                    marginBottom: 6, opacity: futuro ? 0.35 : 1,
                                                    transition: 'opacity 0.3s',
                                                }}>
                                                    <span style={{ fontSize: 18, minWidth: 22, textAlign: 'center' }}>
                                                        {feito  && '✅'}
                                                        {ativo  && <FaRobot size={16} style={{ color: 'var(--fin-ac)', animation: 'spin 1s linear infinite' }} />}
                                                        {futuro && '○'}
                                                    </span>
                                                    <span style={{
                                                        fontWeight: ativo ? 700 : 400,
                                                        color: ativo ? 'var(--fin-ac)' : feito ? 'var(--fin-ok)' : 'var(--fin-mt)',
                                                        fontSize: '0.95rem',
                                                    }}>
                                                        {passo.label}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()
                        ) : resultadoExtracao ? (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    {resultadoExtracao.metodo === 'xml' && (
                                        <span className="fin-pp__pill is-ok" style={{ fontSize: '0.8rem' }}>
                                            <FaFileCode size={13} /> XML lido ✅ — {resultadoExtracao.totalCampos} campos extraídos
                                        </span>
                                    )}
                                    {resultadoExtracao.metodo === 'gpt_vision' && (
                                        <span className="fin-pp__pill is-info" style={{ fontSize: '0.8rem' }}>
                                            <FaRobot size={13} /> PDF analisado por GPT-4o 🤖 — {resultadoExtracao.totalCampos} campos extraídos
                                        </span>
                                    )}
                                    {resultadoExtracao.metodo === 'claude_vision' && (
                                        <span className="fin-pp__pill is-info" style={{ fontSize: '0.8rem' }}>
                                            <FaRobot size={13} /> PDF analisado por Claude 🤖 — {resultadoExtracao.totalCampos} campos extraídos
                                        </span>
                                    )}
                                    {(resultadoExtracao.metodo === 'pdf_sem_chave' || resultadoExtracao.metodo === 'manual') && (
                                        <span className="fin-pp__pill is-warn" style={{ fontSize: '0.8rem' }}>
                                            ⚠ Arquivo salvo — preencha os dados manualmente
                                        </span>
                                    )}
                                    {resultadoExtracao.nome_emitente && (
                                        <span style={{ color: 'var(--fin-mt)', fontSize: '0.85rem' }}>
                                            Emitente: <strong>{resultadoExtracao.nome_emitente}</strong>
                                            {resultadoExtracao.cnpj_emitente && <> · <strong>{resultadoExtracao.cnpj_emitente}</strong></>}
                                        </span>
                                    )}
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                        style={{ fontSize: '0.78rem' }}
                                        onClick={e => { e.stopPropagation(); inputArquivoRef.current && inputArquivoRef.current.click(); }}>
                                        Enviar outro arquivo
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{ color: 'var(--fin-ac)' }}>
                                <FaUpload size={28} style={{ marginBottom: 8 }} />
                                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
                                    Arraste o XML ou PDF da nota fiscal aqui
                                </div>
                                <div style={{ color: 'var(--fin-mt)', fontSize: '0.82rem' }}>
                                    <FaFileCode size={12} /> XML NF-e/NFS-e → extração automática completa &nbsp;|&nbsp;
                                    <FaFilePdf size={12} /> PDF → extração por IA (Claude Vision)
                                </div>
                                <div style={{ color: 'var(--fin-mt)', fontSize: '0.78rem', marginTop: 4 }}>
                                    ou clique aqui para selecionar o arquivo
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Banner campos auto-preenchidos */}
                {Object.keys(camposAutoPreenchidos).length > 0 && (
                    <div className="fin-pp__card" style={{
                        marginBottom: 16,
                        padding: '10px 16px',
                        background: 'rgba(21,128,61,0.07)',
                        borderLeft: '4px solid var(--fin-ok)',
                        fontSize: '0.82rem',
                        color: 'var(--fin-ok)',
                    }}>
                        ✏ Campos marcados com <strong style={{ background: 'rgba(21,128,61,0.15)', borderRadius: 4, padding: '0 4px' }}>fundo verde</strong> foram preenchidos automaticamente — confirme antes de salvar.
                    </div>
                )}

                {/* ── Layout duas colunas ──────────────────────────────────── */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>

                    {/* ── Coluna esquerda — dados básicos ──────────────────── */}
                    <div style={{ flex: '1 1 580px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>

                        {/* Card: Dados do Documento */}
                        <div className="fin-pp__card">
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaFileInvoice size={16} /></span>
                                <span className="fin-pp__cphead-title">Dados do Documento</span>
                            </div>
                            <div style={{ padding: '16px 20px' }}>
                                <div className="fin-pp__filters">

                                    {/* Fornecedor */}
                                    <div className="fin-pp__field" style={{ flex: '1 1 100%' }}>
                                        <label className="fin-pp__label">
                                            Fornecedor *{autoTag('fornecedor_id')}
                                        </label>
                                        <ReactSelect
                                            loadOptions={(input) =>
                                                api({ method: 'get', action: '/participante', token, params: { nome: input, nomefantasia: input, tipoCadastro: ['orcamento'], limit: 30 } })
                                                    .then(response =>
                                                        (response.data.data || []).map(f => ({
                                                            value: f.id,
                                                            label: [f.nomefantasia || f.nome, f.cnpj ? ('CNPJ: ' + f.cnpj) : ''].filter(Boolean).join(' — '),
                                                        }))
                                                    )
                                            }
                                            value={dados.fornecedor_id ? { value: dados.fornecedor_id, label: dados.nomeFornecedor } : null}
                                            onChange={opt => setDados(prev => ({
                                                ...prev,
                                                fornecedor_id: opt ? opt.value : '',
                                                nomeFornecedor: opt ? opt.label : '',
                                                retencoes: [],
                                            }))}
                                            placeholder="Buscar fornecedor pelo nome ou CNPJ..."
                                        />
                                    </div>

                                    {/* Evento */}
                                    <div className="fin-pp__field" style={{ flex: '1 1 280px' }}>
                                        <label className="fin-pp__label">Evento</label>
                                        <ReactSelect
                                            loadOptions={(input) =>
                                                api({ method: 'get', action: '/evento', token, params: { nome: input, limit: 30 } })
                                                    .then(response =>
                                                        (response.data.data || []).map(e => ({
                                                            value: e.id,
                                                            label: (e.codigo ? e.codigo + ' — ' : '') + e.nome,
                                                        }))
                                                    )
                                            }
                                            value={dados.evento_id ? { value: dados.evento_id, label: dados.nomeEvento } : null}
                                            onChange={opt => setDados(prev => ({ ...prev, evento_id: opt ? opt.value : '', nomeEvento: opt ? opt.label : '' }))}
                                            isClearable
                                            placeholder="Buscar evento..."
                                        />
                                    </div>

                                    {/* Tipo */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 200px' }}>
                                        <label className="fin-pp__label">Tipo</label>
                                        <ReactSelect
                                            options={TIPO_OPTIONS}
                                            value={TIPO_OPTIONS.find(o => o.value === dados.tipo) || TIPO_OPTIONS[0]}
                                            onChange={opt => set('tipo', opt ? opt.value : 'nfse')}
                                        />
                                    </div>

                                    {/* Status */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 200px' }}>
                                        <label className="fin-pp__label">Status</label>
                                        <ReactSelect
                                            options={STATUS_OPTIONS}
                                            value={STATUS_OPTIONS.find(o => o.value === dados.status) || STATUS_OPTIONS[0]}
                                            onChange={opt => set('status', opt ? opt.value : 'aguardando')}
                                        />
                                    </div>

                                    {/* NF Substituta */}
                                    {dados.status === 'substituida' && (
                                        <div className="fin-pp__field" style={{ flex: '1 1 100%' }}>
                                            <label className="fin-pp__label" style={{ color: 'var(--fin-ok)' }}>
                                                🔄 NF que a substitui
                                                <span style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: 6, color: 'var(--fin-mt)' }}>
                                                    (a nova NF emitida pelo fornecedor)
                                                </span>
                                            </label>
                                            {dados.substituida_por_id && dados.substituida_por_label ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                                    <span className="fin-pp__pill is-muted" style={{ fontSize: '0.85rem' }}>
                                                        🔄 {dados.substituida_por_label}
                                                    </span>
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                        onClick={() => setDados(prev => ({ ...prev, substituida_por_id: null, substituida_por_label: '' }))}>
                                                        ✕ Remover
                                                    </button>
                                                    <a href={'/cadastroNotaFiscal/' + dados.substituida_por_id}
                                                        target="_blank" rel="noreferrer"
                                                        className="fin-pp__btn fin-pp__btn--sm">
                                                        Ver NF →
                                                    </a>
                                                </div>
                                            ) : (
                                                <ReactSelect
                                                    loadOptions={(input) =>
                                                        api({ method: 'get', action: '/notaFiscal', token, params: { numero: input || '', fornecedor_id: dados.fornecedor_id || undefined, limit: 30 } })
                                                            .then(resp =>
                                                                (resp.data?.data || [])
                                                                    .filter(nf => String(nf.id) !== String(id))
                                                                    .map(nf => ({
                                                                        value: String(nf.id),
                                                                        label: 'NF ' + (nf.numero || '?') + (nf.serie ? '/' + nf.serie : '') + (nf.fornecedor ? ' — ' + (nf.fornecedor.nomefantasia || nf.fornecedor.nome) : ''),
                                                                    }))
                                                            )
                                                    }
                                                    value={null}
                                                    onChange={opt => { if (opt) setDados(prev => ({ ...prev, substituida_por_id: opt.value, substituida_por_label: opt.label.replace(/^NF /, 'NF ') })); }}
                                                    placeholder="Buscar a NF substituta pelo número..."
                                                    isClearable
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* Número */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 160px' }}>
                                        <label className="fin-pp__label">Número da NF{autoTag('numero')}</label>
                                        <input type="text" className="fin-pp__input" style={{ paddingLeft: 14, ...estiloAutoPreenchido('numero') }}
                                            placeholder="Ex.: 001234" value={dados.numero} onChange={e => set('numero', e.target.value)} />
                                    </div>

                                    {/* Série */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 100px' }}>
                                        <label className="fin-pp__label">Série{autoTag('serie')}</label>
                                        <input type="text" className="fin-pp__input" style={{ paddingLeft: 14, ...estiloAutoPreenchido('serie') }}
                                            placeholder="Ex.: A" value={dados.serie} onChange={e => set('serie', e.target.value)} />
                                    </div>

                                    {/* Data emissão */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 180px' }}>
                                        <label className="fin-pp__label">Data de Emissão{autoTag('data_emissao')}</label>
                                        <input type="date" className="fin-pp__input" style={{ paddingLeft: 14, ...estiloAutoPreenchido('data_emissao') }}
                                            value={dados.data_emissao || ''} onChange={e => set('data_emissao', e.target.value)} />
                                    </div>

                                    {/* Competência */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 180px' }}>
                                        <label className="fin-pp__label">Competência{autoTag('data_competencia')}</label>
                                        <input type="date" className="fin-pp__input" style={{ paddingLeft: 14, ...estiloAutoPreenchido('data_competencia') }}
                                            value={dados.data_competencia || ''} onChange={e => set('data_competencia', e.target.value)} />
                                    </div>

                                    {/* Retroativa */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 auto', alignSelf: 'flex-end', paddingBottom: 6 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', color: 'var(--fin-wn)', fontWeight: 600, fontSize: '0.85rem' }}>
                                            <input type="checkbox" id="is_retroativa"
                                                checked={Boolean(dados.is_retroativa)}
                                                onChange={e => set('is_retroativa', e.target.checked)}
                                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                                            />
                                            ⚠ Retroativa
                                        </label>
                                    </div>

                                    {/* NBS */}
                                    <div className="fin-pp__field" style={{ flex: '1 1 220px' }}>
                                        <label className="fin-pp__label">
                                            NBS <span style={{ fontWeight: 400, color: 'var(--fin-mt)', fontSize: '0.78rem' }}>(sobrescreve o do fornecedor)</span>
                                            {autoTag('nbs')}
                                        </label>
                                        <input type="text" className="fin-pp__input" style={{ paddingLeft: 14, ...estiloAutoPreenchido('nbs') }}
                                            placeholder="Ex.: 1.0101.00.00" value={dados.nbs} onChange={e => set('nbs', e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card: Identificação Eletrônica */}
                        <div className="fin-pp__card">
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic"><FaFileCode size={15} /></span>
                                <span className="fin-pp__cphead-title">Identificação Eletrônica</span>
                            </div>
                            <div style={{ padding: '16px 20px' }}>
                                <div className="fin-pp__filters">

                                    {/* Chave NF-e */}
                                    <div className="fin-pp__field" style={{ flex: '1 1 340px' }}>
                                        <label className="fin-pp__label">
                                            Chave NF-e <span style={{ fontWeight: 400, color: 'var(--fin-mt)', fontSize: '0.78rem' }}>(44 dígitos)</span>
                                            {autoTag('chave_nfe')}
                                        </label>
                                        <input type="text" className="fin-pp__input" style={{ paddingLeft: 14, fontFamily: 'monospace', fontSize: '0.78rem', ...estiloAutoPreenchido('chave_nfe') }}
                                            maxLength={44} placeholder="00000000000000000000000000000000000000000000"
                                            value={dados.chave_nfe} onChange={e => set('chave_nfe', e.target.value.replace(/\D/g, ''))} />
                                    </div>

                                    {/* Protocolo NFS-e */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 180px' }}>
                                        <label className="fin-pp__label">Protocolo NFS-e{autoTag('protocolo_nfs')}</label>
                                        <input type="text" className="fin-pp__input" style={{ paddingLeft: 14, ...estiloAutoPreenchido('protocolo_nfs') }}
                                            placeholder="Número do protocolo" value={dados.protocolo_nfs} onChange={e => set('protocolo_nfs', e.target.value)} />
                                    </div>

                                    {/* Link NFS-e */}
                                    <div className="fin-pp__field" style={{ flex: '1 1 300px' }}>
                                        <label className="fin-pp__label">Link NFS-e</label>
                                        <input type="url" className="fin-pp__input" style={{ paddingLeft: 14 }}
                                            placeholder="https://..." value={dados.link_nfs} onChange={e => set('link_nfs', e.target.value)} />
                                    </div>

                                    {/* Arquivo PDF/XML */}
                                    <div className="fin-pp__field" style={{ flex: '0 0 240px' }}>
                                        <label className="fin-pp__label">Arquivo PDF/XML</label>
                                        {dados.arquivo_id && dados.nomeArquivo ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <a href={dados.linkArquivo || ('#arquivo-' + dados.arquivo_id)} target="_blank" rel="noreferrer"
                                                    className="fin-pp__btn fin-pp__btn--sm"
                                                    style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}
                                                    title={dados.nomeArquivo}>
                                                    <FaDownload size={12} />
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                                                        {dados.nomeArquivo}
                                                    </span>
                                                </a>
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                    title="Remover arquivo"
                                                    onClick={() => setDados(prev => ({ ...prev, arquivo_id: '', nomeArquivo: '', linkArquivo: '' }))}>
                                                    <FaTrash size={11} />
                                                </button>
                                            </div>
                                        ) : (
                                            <div>
                                                <input type="file" className="fin-pp__input" style={{ paddingLeft: 14, paddingTop: 10, height: 'auto' }}
                                                    accept=".pdf,.xml" disabled={uploadando}
                                                    onChange={e => uploadArquivo(e.target.files[0])} />
                                                {uploadando && <div style={{ fontSize: '0.78rem', color: 'var(--fin-mt)', marginTop: 4 }}>Enviando…</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card: Observação */}
                        <div className="fin-pp__card">
                            <div style={{ padding: '16px 20px' }}>
                                <label className="fin-pp__label">Observação</label>
                                <textarea className="fin-pp__input"
                                    style={{ height: 'auto', padding: '10px 14px', resize: 'vertical', minHeight: 80 }}
                                    rows={3} value={dados.observacao} onChange={e => set('observacao', e.target.value)}
                                    placeholder="Informações adicionais sobre esta nota fiscal..." />
                            </div>
                        </div>
                    </div>

                    {/* ── Coluna direita — valores, retenções, salvar ─────── */}
                    <div style={{ flex: '0 0 340px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>

                        {/* Card: Valores */}
                        <div className="fin-pp__card">
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic is-ok">💰</span>
                                <span className="fin-pp__cphead-title">Valores</span>
                            </div>
                            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                                {/* Valor Bruto */}
                                <div>
                                    <label className="fin-pp__label">Valor Bruto *{autoTag('valor_bruto')}</label>
                                    <NumericFormat
                                        className="fin-pp__input"
                                        style={{ paddingLeft: 14, ...estiloAutoPreenchido('valor_bruto') }}
                                        thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
                                        prefix="R$ " value={dados.valor_bruto}
                                        onValueChange={v => set('valor_bruto', v.floatValue ?? '')}
                                        placeholder="R$ 0,00"
                                    />
                                </div>

                                {/* Adiantamento conciliado (read-only) */}
                                <div>
                                    <label className="fin-pp__label" style={{ color: 'var(--fin-mt)' }}>Adiantamento conciliado</label>
                                    <div className="fin-pp__input" style={{ paddingLeft: 14, background: 'var(--fin-sf)', color: 'var(--fin-mt)', cursor: 'default', display: 'flex', alignItems: 'center' }}>
                                        {moeda(dados.valor_adiantamento || 0)}
                                    </div>
                                    <div style={{ fontSize: '0.74rem', color: 'var(--fin-mt)', marginTop: 3 }}>Calculado automaticamente das distribuições do item</div>
                                </div>

                                {/* Base de cálculo (read-only) */}
                                <div>
                                    <label className="fin-pp__label" style={{ color: 'var(--fin-mt)' }}>Base de cálculo</label>
                                    <div className="fin-pp__input" style={{ paddingLeft: 14, background: 'var(--fin-sf)', color: 'var(--fin-mt)', cursor: 'default', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {moeda(dados.valor_base_calculo || 0)}
                                        {dados.valor_base_calculo > 0 && (
                                            <span style={{ fontSize: '0.74rem' }}>(bruto − adiantamento)</span>
                                        )}
                                    </div>
                                </div>

                                {/* Botão calcular retenções */}
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                    style={{ width: '100%' }}
                                    onClick={calcularRetencoes} disabled={calculando || !dados.fornecedor_id}>
                                    <FaCalculator size={14} style={{ marginRight: 6 }} />
                                    {calculando ? 'Calculando…' : 'Calcular Retenções'}
                                </button>

                                {/* Resumo bruto / retenções / líquido */}
                                <div style={{ background: 'var(--fin-sf)', border: '1px solid var(--fin-bd)', borderRadius: 8, padding: '12px 14px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.87rem' }}>
                                        <span style={{ color: 'var(--fin-mt)' }}>Valor Bruto:</span>
                                        <strong>{moeda(valorBrutoNum)}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.87rem', color: 'var(--fin-dg)' }}>
                                        <span>Total Retenções:</span>
                                        <strong>− {moeda(dados.valor_retencoes || 0)}</strong>
                                    </div>
                                    <div style={{ borderTop: '1px solid var(--fin-bd)', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--fin-mt)', fontSize: '0.87rem' }}>Valor Líquido:</span>
                                        <strong style={{ fontSize: '1.05rem', color: 'var(--fin-ok)' }}>{moeda(valorLiquidoCalc)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card: Retenções */}
                        <div className="fin-pp__card">
                            <div className="fin-pp__cphead">
                                <span className="fin-pp__cphead-ic is-danger">🧾</span>
                                <span className="fin-pp__cphead-title">Retenções</span>
                                {dados.retencoes.length === 0 && (
                                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--fin-mt)' }}>
                                        Clique em "Calcular" →
                                    </span>
                                )}
                            </div>
                            <div style={{ padding: '12px 16px' }}>
                                {dados.retencoes.length === 0 ? (
                                    <div className="fin-pp__empty" style={{ padding: '20px 0' }}>
                                        Selecione o fornecedor e o valor bruto,<br />
                                        depois clique em <strong>Calcular Retenções</strong>.
                                    </div>
                                ) : (
                                    <div className="fin-pp__tblwrap">
                                        <table className="fin-pp__tbl" style={{ minWidth: 0 }}>
                                            <thead>
                                                <tr>
                                                    <th>Tributo</th>
                                                    <th style={{ textAlign: 'right', width: 70 }}>Alíq. %</th>
                                                    <th style={{ textAlign: 'right', width: 90 }}>Valor R$</th>
                                                    <th style={{ width: 32 }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dados.retencoes.map((ret, i) => (
                                                    <tr key={ret.tipo} style={ret.eh_override ? { background: '#fff8e1' } : {}}>
                                                        <td>
                                                            <strong style={{ fontSize: '0.82rem' }}>{TRIBUTOS_LABELS[ret.tipo] || ret.tipo}</strong>
                                                            {ret.eh_override && (
                                                                <span title="Valor ajustado manualmente" style={{ color: 'var(--fin-wn)', fontSize: '0.7rem', marginLeft: 4 }}>✏</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <input type="number" step="0.0001" min="0" max="100"
                                                                className="fin-pp__input"
                                                                style={{ paddingLeft: 8, height: 32, textAlign: 'right', fontSize: '0.82rem' }}
                                                                value={ret.aliquota}
                                                                onChange={e => setRetencao(ret.tipo, 'aliquota', parseFloat(e.target.value) || 0)} />
                                                        </td>
                                                        <td>
                                                            <input type="number" step="0.01" min="0"
                                                                className="fin-pp__input"
                                                                style={{ paddingLeft: 8, height: 32, textAlign: 'right', fontSize: '0.82rem' }}
                                                                value={ret.valor}
                                                                onChange={e => setRetencao(ret.tipo, 'valor', parseFloat(e.target.value) || 0)} />
                                                        </td>
                                                        <td>
                                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                                style={{ padding: '2px 6px' }}
                                                                title="Remover retenção"
                                                                onClick={() => {
                                                                    const novas = dados.retencoes.filter((_, j) => j !== i);
                                                                    const total = novas.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
                                                                    setDados(prev => ({ ...prev, retencoes: novas, valor_retencoes: total, valor_liquido: Math.max(0, valorBrutoNum - total) }));
                                                                }}>
                                                                <FaTrash size={11} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ background: 'rgba(198,40,40,0.06)' }}>
                                                    <td colSpan={2} style={{ fontSize: '0.82rem' }}><strong>Total</strong></td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <strong style={{ color: 'var(--fin-dg)', fontSize: '0.85rem' }}>
                                                            {moeda(dados.valor_retencoes || 0)}
                                                        </strong>
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}

                                {/* Adicionar tributo manual */}
                                <div style={{ marginTop: 10 }}>
                                    <select className="fin-pp__select" style={{ width: '100%', height: 36 }}
                                        defaultValue=""
                                        onChange={e => {
                                            const tipo = e.target.value;
                                            if (!tipo) return;
                                            if (dados.retencoes.find(r => r.tipo === tipo)) {
                                                toast.warning('Retenção ' + tipo.toUpperCase() + ' já está na lista.');
                                                e.target.value = '';
                                                return;
                                            }
                                            setRetencao(tipo, 'valor', 0);
                                            e.target.value = '';
                                        }}>
                                        <option value="">+ Adicionar tributo manualmente...</option>
                                        {Object.entries(TRIBUTOS_LABELS).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Botão Salvar */}
                        <div className="fin-pp__card" style={{ padding: '16px 20px' }}>
                            <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                style={{ width: '100%', height: 48, fontSize: '1rem', fontWeight: 700 }}
                                onClick={salvar} disabled={salvando}>
                                <FaSave size={17} style={{ marginRight: 8 }} />
                                {salvando ? 'Salvando…' : 'Salvar Nota Fiscal'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Itens do orçamento vinculados (só quando editando) ───── */}
                {editando && (
                    <div className="fin-pp__card" style={{ marginTop: 16, overflow: 'hidden' }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaFileCode size={15} /></span>
                            <span className="fin-pp__cphead-title">Itens do Orçamento vinculados a esta NF</span>
                            <div style={{ marginLeft: 'auto' }}>
                                <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary"
                                    onClick={abrirFormVincular}>
                                    <FaPlus size={11} style={{ marginRight: 4 }} /> Vincular item
                                </button>
                            </div>
                        </div>

                        {/* Formulário inline de vínculo */}
                        {showFormVincular && (
                            <div style={{ padding: '14px 20px', background: 'var(--fin-sf)', borderBottom: '1px solid var(--fin-bd)' }}>
                                <div className="fin-pp__filters">
                                    <div className="fin-pp__field" style={{ flex: '1 1 260px' }}>
                                        <label className="fin-pp__label" style={{ fontSize: '0.8rem' }}>Item do Orçamento</label>
                                        <ReactSelect
                                            options={itensOpcoes}
                                            isLoading={carregandoOpcoes}
                                            value={novoItem.orcamentoitem}
                                            onChange={opt => setNovoItem(prev => ({ ...prev, orcamentoitem: opt }))}
                                            placeholder={carregandoOpcoes ? 'Carregando itens...' : 'Selecionar item...'}
                                            isClearable
                                        />
                                    </div>
                                    <div className="fin-pp__field" style={{ flex: '0 0 160px' }}>
                                        <label className="fin-pp__label" style={{ fontSize: '0.8rem' }}>Valor alocado (R$)</label>
                                        <NumericFormat
                                            className="fin-pp__input" style={{ paddingLeft: 14 }}
                                            thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
                                            prefix="R$ " placeholder="R$ 0,00"
                                            value={novoItem.valor_alocado}
                                            onValueChange={v => setNovoItem(prev => ({ ...prev, valor_alocado: v.floatValue || '' }))}
                                        />
                                    </div>
                                    <div className="fin-pp__field" style={{ flex: '1 1 180px' }}>
                                        <label className="fin-pp__label" style={{ fontSize: '0.8rem' }}>Observação</label>
                                        <input type="text" className="fin-pp__input" style={{ paddingLeft: 14 }}
                                            placeholder="Opcional" value={novoItem.observacao}
                                            onChange={e => setNovoItem(prev => ({ ...prev, observacao: e.target.value }))} />
                                    </div>
                                    <div className="fin-pp__field" style={{ flex: '0 0 auto', alignSelf: 'flex-end', display: 'flex', gap: 6 }}>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--ok fin-pp__btn--tall"
                                            onClick={vincularItemNF} disabled={salvandoItem || !novoItem.orcamentoitem}>
                                            {salvandoItem ? '…' : '✔ Salvar'}
                                        </button>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--tall"
                                            onClick={() => setShowFormVincular(false)}>✕</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Lista de itens vinculados */}
                        {carregandoItens ? (
                            <div className="fin-pp__empty">Carregando…</div>
                        ) : itensVinculados.length === 0 ? (
                            <div className="fin-pp__empty">
                                Nenhum item do orçamento vinculado a esta NF ainda.<br />
                                <small style={{ color: 'var(--fin-mt)' }}>Use o botão <strong>+ Vincular item</strong> para associar os itens que esta NF cobre.</small>
                            </div>
                        ) : (
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl" style={{ minWidth: 800 }}>
                                    <thead>
                                        <tr>
                                            <th>Orçamento</th>
                                            <th>Item</th>
                                            <th>Fornecedor</th>
                                            <th style={{ textAlign: 'right', width: 130 }}>Vlr. Fechamento</th>
                                            <th style={{ textAlign: 'right', width: 120 }}>Vlr. Alocado</th>
                                            <th>Obs.</th>
                                            <th style={{ width: 36 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itensVinculados.map(vinculo => {
                                            const oi = vinculo.orcamentoitem || {};
                                            const descricao = [oi.codigoFornecedor, oi.produto_nome].filter(Boolean).join(' — ') || ('Item #' + vinculo.orcamentoitem_id);
                                            return (
                                                <tr key={vinculo.id}>
                                                    <td className="fin-pp__cell-muted" style={{ maxWidth: 160 }}>
                                                        <span title={oi.orcamento_label || '—'} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {oi.orcamento_label || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="fin-pp__cell-strong" style={{ maxWidth: 180 }}>
                                                        <span title={descricao} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {descricao}
                                                        </span>
                                                    </td>
                                                    <td className="fin-pp__cell-muted" style={{ maxWidth: 140 }}>
                                                        <span title={oi.fornecedor_nome || '—'} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {oi.fornecedor_nome || '—'}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <span className="fin-pp__cell-muted">{oi.totalFechamento ? moeda(oi.totalFechamento) : '—'}</span>
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--fin-ok)' }}>
                                                        {vinculo.valor_alocado ? moeda(vinculo.valor_alocado) : '—'}
                                                    </td>
                                                    <td className="fin-pp__cell-muted" style={{ maxWidth: 120 }}>
                                                        <span title={vinculo.observacao || ''} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {vinculo.observacao || ''}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                            style={{ padding: '2px 6px' }}
                                                            title="Remover vínculo"
                                                            onClick={() => desvincularItemNF(vinculo.id, descricao)}>
                                                            <FaTrash size={11} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}
